import React, { useState, useEffect } from "react";

const QuestionTypeWeightings = ({ profile, handleChange }) => {
  const [weightings, setWeightings] = useState({
    multipleChoice: 5,
    openEnded: 4,
    scenarioBased: 7,
    technicalSkills: 8,
    behavioral: 6,
    documentUpload: 2,
  });

  const [percentages, setPercentages] = useState({
    multipleChoice: 0,
    openEnded: 0,
    scenarioBased: 0,
    technicalSkills: 0,
    behavioral: 0,
    documentUpload: 0,
  });

  useEffect(() => {
    updateDistribution();
  }, [weightings]);

  useEffect(() => {
    setWeightings({
      multipleChoice: profile.multiple_choice || 5,
      openEnded: profile.open_ended || 4,
      scenarioBased: profile.scenario_based || 7,
      technicalSkills: profile.technical_skills || 8,
      behavioral: profile.behavioral || 6,
      documentUpload: profile.document_upload || 2,
    });
  }, [profile]);

  const updateDistribution = () => {
    const total = Object.values(weightings).reduce(
      (sum, value) => sum + value,
      0,
    );

    if (total === 0) {
      setPercentages({
        multipleChoice: 0,
        openEnded: 0,
        scenarioBased: 0,
        technicalSkills: 0,
        behavioral: 0,
        documentUpload: 0,
      });
      return;
    }

    const newPercentages = {};
    Object.keys(weightings).forEach((key) => {
      newPercentages[key] = Math.round((weightings[key] / total) * 100);
    });

    setPercentages(newPercentages);
  };

  const handleWeightingChange = (type, value) => {
    setWeightings((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  const setPreset = (preset) => {
    if (preset === "technical") {
      setWeightings({
        multipleChoice: 4,
        openEnded: 3,
        scenarioBased: 8,
        technicalSkills: 10,
        behavioral: 3,
        documentUpload: 4,
      });
    } else if (preset === "manager") {
      setWeightings({
        multipleChoice: 3,
        openEnded: 7,
        scenarioBased: 8,
        technicalSkills: 4,
        behavioral: 9,
        documentUpload: 3,
      });
    } else if (preset === "balanced") {
      setWeightings({
        multipleChoice: 5,
        openEnded: 4,
        scenarioBased: 7,
        technicalSkills: 8,
        behavioral: 6,
        documentUpload: 2,
      });
    }
  };

  return (
    <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden transform transition-all duration-300 hover:shadow-md">
      <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-primary-50 to-white">
        <h2 className="text-lg font-medium text-gray-900 flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2 text-primary-600"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
          </svg>
          Question Type Weightings
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Adjust importance for different question types (0-10 scale)
        </p>
      </div>
      <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100 mb-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-yellow-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-yellow-800">
                Weighting Guidelines
              </h4>
              <p className="mt-1 text-sm text-yellow-700">
                Higher values (7-10) will include more questions of this type.
                Setting a value to 0 will exclude this question type entirely.
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end mb-6">
          <div className="inline-flex rounded-md shadow-sm">
            <button
              type="button"
              onClick={() => setPreset("technical")}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-l-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-2 text-blue-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                  clipRule="evenodd"
                />
              </svg>
              Technical Role
            </button>
            <button
              type="button"
              onClick={() => setPreset("manager")}
              className="inline-flex items-center px-4 py-2 border border-gray-300 border-l-0 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-2 text-purple-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
              </svg>
              Management Role
            </button>
            <button
              type="button"
              onClick={() => setPreset("balanced")}
              className="inline-flex items-center px-4 py-2 border border-gray-300 border-l-0 rounded-r-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-2 text-green-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z" />
              </svg>
              Balanced
            </button>
          </div>
        </div>

        {/* Question Type Distribution Visualization */}
        <div className="mb-6 p-5 bg-gray-50 border border-gray-200 rounded-lg shadow-inner">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            Question Type Distribution
          </h3>
          <div className="flex h-10 w-full rounded-md overflow-hidden shadow-sm">
            <div
              className="bg-blue-500 transition-all duration-300 ease-in-out text-xs text-white flex items-center justify-center"
              style={{ width: `${percentages.multipleChoice}%` }}
            >
              {percentages.multipleChoice > 5
                ? `${percentages.multipleChoice}%`
                : ""}
            </div>
            <div
              className="bg-green-500 transition-all duration-300 ease-in-out text-xs text-white flex items-center justify-center"
              style={{ width: `${percentages.openEnded}%` }}
            >
              {percentages.openEnded > 5 ? `${percentages.openEnded}%` : ""}
            </div>
            <div
              className="bg-purple-500 transition-all duration-300 ease-in-out text-xs text-white flex items-center justify-center"
              style={{ width: `${percentages.scenarioBased}%` }}
            >
              {percentages.scenarioBased > 5
                ? `${percentages.scenarioBased}%`
                : ""}
            </div>
            <div
              className="bg-yellow-500 transition-all duration-300 ease-in-out text-xs text-white flex items-center justify-center"
              style={{ width: `${percentages.technicalSkills}%` }}
            >
              {percentages.technicalSkills > 5
                ? `${percentages.technicalSkills}%`
                : ""}
            </div>
            <div
              className="bg-pink-500 transition-all duration-300 ease-in-out text-xs text-white flex items-center justify-center"
              style={{ width: `${percentages.behavioral}%` }}
            >
              {percentages.behavioral > 5 ? `${percentages.behavioral}%` : ""}
            </div>
            <div
              className="bg-indigo-500 transition-all duration-300 ease-in-out text-xs text-white flex items-center justify-center"
              style={{ width: `${percentages.documentUpload}%` }}
            >
              {percentages.documentUpload > 5
                ? `${percentages.documentUpload}%`
                : ""}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-1.5"></span>
              Multiple Choice:{" "}
              <span className="font-semibold ml-0.5">
                {percentages.multipleChoice}
              </span>
              %
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5"></span>
              Open Ended:{" "}
              <span className="font-semibold ml-0.5">
                {percentages.openEnded}
              </span>
              %
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              <span className="w-2 h-2 bg-purple-500 rounded-full mr-1.5"></span>
              Scenario:{" "}
              <span className="font-semibold ml-0.5">
                {percentages.scenarioBased}
              </span>
              %
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              <span className="w-2 h-2 bg-yellow-500 rounded-full mr-1.5"></span>
              Technical:{" "}
              <span className="font-semibold ml-0.5">
                {percentages.technicalSkills}
              </span>
              %
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
              <span className="w-2 h-2 bg-pink-500 rounded-full mr-1.5"></span>
              Behavioral:{" "}
              <span className="font-semibold ml-0.5">
                {percentages.behavioral}
              </span>
              %
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
              <span className="w-2 h-2 bg-indigo-500 rounded-full mr-1.5"></span>
              Document:{" "}
              <span className="font-semibold ml-0.5">
                {percentages.documentUpload}
              </span>
              %
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="bg-white p-4 border border-gray-100 rounded-lg shadow-sm hover:shadow transition-all duration-200 transform hover:-translate-y-1">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-blue-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <label
                  htmlFor="multipleChoice"
                  className="block text-sm font-medium text-gray-700"
                >
                  Multiple Choice
                </label>
                <p className="text-xs text-gray-500">
                  Standard questions with predefined answer options
                </p>
              </div>
            </div>
            <div className="mt-3 flex items-center">
              <input
                type="range"
                id="multipleChoice"
                min="0"
                max="10"
                step="1"
                className="w-full"
                value={weightings.multipleChoice}
                onChange={(e) => {
                  const value = e.target.value;
                  profile.multiple_choice = parseInt(value);
                  handleChange(profile, "question_weight");
                }}
              />
              <span className="ml-2 text-sm font-medium text-blue-700 w-6 text-center">
                {weightings.multipleChoice}
              </span>
            </div>
          </div>

          <div className="bg-white p-4 border border-gray-100 rounded-lg shadow-sm hover:shadow transition-all duration-200 transform hover:-translate-y-1">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3 flex-shrink-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-green-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </div>
              <div>
                <label
                  htmlFor="openEnded"
                  className="block text-sm font-medium text-gray-700"
                >
                  Open Ended
                </label>
                <p className="text-xs text-gray-500">
                  Free-form responses to assess communication skills
                </p>
              </div>
            </div>
            <div className="mt-3 flex items-center">
              <input
                type="range"
                id="openEnded"
                min="0"
                max="10"
                step="1"
                className="w-full"
                value={weightings.openEnded}
                onChange={(e) => {
                  const value = e.target.value;
                  profile.open_ended = parseInt(value);
                  handleChange(profile, "question_weight");
                }}
              />
              <span className="ml-2 text-sm font-medium text-green-700 w-6 text-center">
                {weightings.openEnded}
              </span>
            </div>
          </div>

          <div className="bg-white p-4 border border-gray-100 rounded-lg shadow-sm hover:shadow transition-all duration-200 transform hover:-translate-y-1">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-3 flex-shrink-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-purple-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
              </div>
              <div>
                <label
                  htmlFor="scenarioBased"
                  className="block text-sm font-medium text-gray-700"
                >
                  Scenario Based
                </label>
                <p className="text-xs text-gray-500">
                  Problem-solving questions with real-world scenarios
                </p>
              </div>
            </div>
            <div className="mt-3 flex items-center">
              <input
                type="range"
                id="scenarioBased"
                min="0"
                max="10"
                step="1"
                className="w-full"
                value={weightings.scenarioBased}
                onChange={(e) => {
                  const value = e.target.value;
                  profile.scenario_based = parseInt(value);
                  handleChange(profile, "question_weight");
                }}
              />
              <span className="ml-2 text-sm font-medium text-purple-700 w-6 text-center">
                {weightings.scenarioBased}
              </span>
            </div>
          </div>

          <div className="bg-white p-4 border border-gray-100 rounded-lg shadow-sm hover:shadow transition-all duration-200 transform hover:-translate-y-1">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center mr-3 flex-shrink-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-yellow-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                  />
                </svg>
              </div>
              <div>
                <label
                  htmlFor="technicalSkills"
                  className="block text-sm font-medium text-gray-700"
                >
                  Technical Skills
                </label>
                <p className="text-xs text-gray-500">
                  Job-specific knowledge and practical skills assessment
                </p>
              </div>
            </div>
            <div className="mt-3 flex items-center">
              <input
                type="range"
                id="technicalSkills"
                min="0"
                max="10"
                step="1"
                className="w-full"
                value={weightings.technicalSkills}
                onChange={(e) => {
                  const value = e.target.value;
                  profile.technical_skills = parseInt(value);
                  handleChange(profile, "question_weight");
                }}
              />
              <span className="ml-2 text-sm font-medium text-yellow-700 w-6 text-center">
                {weightings.technicalSkills}
              </span>
            </div>
          </div>

          <div className="bg-white p-4 border border-gray-100 rounded-lg shadow-sm hover:shadow transition-all duration-200 transform hover:-translate-y-1">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center mr-3 flex-shrink-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-pink-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              </div>
              <div>
                <label
                  htmlFor="behavioral"
                  className="block text-sm font-medium text-gray-700"
                >
                  Behavioral
                </label>
                <p className="text-xs text-gray-500">
                  Questions about past experiences and work style
                </p>
              </div>
            </div>
            <div className="mt-3 flex items-center">
              <input
                type="range"
                id="behavioral"
                min="0"
                max="10"
                step="1"
                className="w-full"
                value={weightings.behavioral}
                onChange={(e) => {
                  const value = e.target.value;
                  profile.behavioral = parseInt(value);
                  handleChange(profile, "question_weight");
                }}
              />
              <span className="ml-2 text-sm font-medium text-pink-700 w-6 text-center">
                {weightings.behavioral}
              </span>
            </div>
          </div>

          <div className="bg-white p-4 border border-gray-100 rounded-lg shadow-sm hover:shadow transition-all duration-200 transform hover:-translate-y-1">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mr-3 flex-shrink-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-indigo-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div>
                <label
                  htmlFor="documentUpload"
                  className="block text-sm font-medium text-gray-700"
                >
                  Document Upload
                </label>
                <p className="text-xs text-gray-500">
                  Capability to request certificates, portfolios, or work
                  samples
                </p>
              </div>
            </div>
            <div className="mt-3 flex items-center">
              <input
                type="range"
                id="documentUpload"
                min="0"
                max="10"
                step="1"
                className="w-full"
                value={weightings.documentUpload}
                onChange={(e) => {
                  const value = e.target.value;
                  profile.document_upload = parseInt(value);
                  handleChange(profile, "question_weight");
                }}
              />
              <span className="ml-2 text-sm font-medium text-indigo-700 w-6 text-center">
                {weightings.documentUpload}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionTypeWeightings;
